<template>
  <TopProgressBar v-if="true" />
  <div class="h-screen">
    <Transition name="fade" :duration="100">
      <div v-if="loading" id="loading-nuxt">
        <DsLoader />
      </div>
    </Transition>
    <NuxtLayout>
      <NuxtPage />
    </NuxtLayout>
    <follow-component v-if="getFollowingOpened" />
  </div>
</template>

<script setup lang="ts">
import { watch, ref } from "vue";
import { useRoute } from "vue-router";
import { storeToRefs } from "pinia";
import { useLocationStore } from "./stores/locationStore";
import { useChatStore } from "./stores/chat";
import { getUnreadMessagesCount } from "./services/chatService";

const followStore = useFollowingStore();

const authStore = useAuthStore();
const chatStore = useChatStore();
const notificationsStore = useNotificationsStore();
const { getFollowingOpened } = storeToRefs(followStore);

const hasRequests = computed(() => {
  return useUiStore().hasRequests;
});

useUiStore().setColorMode(
  useNuxtApp().$colorMode.preference === "dark" ? "dark" : "light",
);

useLocationStore().getAllCountries();
useLocationStore().getAllLanguages();
useLocationStore().getAllTimezones();

const nuxtApp = useNuxtApp();
const loading = ref(true);

const route = useRoute();

nuxtApp.hook("page:start", () => {
  loading.value = true;
});

nuxtApp.hook("page:finish", () => {
  loading.value = false;
});

watch(route, (newRoute, oldRoute) => {
  loading.value = true;
  window.scrollTo(0, 0);

  if (process.client) {
    window.scroll({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  }
});

// WebSocket health check
const healthCheckInterval = ref<number | null>(null);

const checkWebSocketHealth = () => {
  if (!window.Echo?.connector?.pusher) return;

  const connectionState = window.Echo.connector.pusher.connection.state;
  if (connectionState !== "connected") {
    console.log("[WebSocket] Attempting reconnection...");
    window.Echo.connector.pusher.connect();
  }
};

// WebSocket: subscribe to channels when authenticated
const shortName = "MGN";

watch(
  () => authStore.user,
  (user) => {
    if (!user?.uuid || !import.meta.client || !window.Echo) return;

    try {
      // Join platform presence channel
      window.Echo.join(`platform.${shortName}`).error((error: any) => {
        console.error("[Echo] Platform channel error:", error);
      });

      // Listen for user status events
      window.Echo
        .private(`user.${shortName}.${user.uuid}`)
        .listen(".UserStatus", (event: any) => {
          console.log("[Echo] UserStatus:", event);
        });

      // Listen for notifications
      window.Echo
        .private(`user.${shortName}.${user.uuid}`)
        .notification((notification: any) => {
          notification.created_at = nuxtApp.$i18n.t("header.now");
          notificationsStore.addNotification(notification);
        });

      // Listen for incoming chat messages
      window.Echo.private(`user.${shortName}.chats.${user.uuid}`)
        .listen(".NewChatMessage", (chat: any) => {
          chatStore.callNewMessage(chat);
        });

      // Listen for confirmation of sent messages
      window.Echo.private(`user.${shortName}.chats.sendMessage.${user.uuid}`)
        .listen(".NewChatMessage", (chat: any) => {
          chatStore.callNewMessage(chat);
        });

      // Fetch initial unread count
      getUnreadMessagesCount()
        .then((res: any) => {
          if (res?.data?.data !== undefined) {
            chatStore.unreadCount = res.data.data;
          }
        })
        .catch(() => {});

      // Start health check interval
      if (healthCheckInterval.value) clearInterval(healthCheckInterval.value);
      healthCheckInterval.value = window.setInterval(checkWebSocketHealth, 30000);
    } catch (err) {
      console.warn("[Echo] Failed to initialize WebSocket channels:", err);
    }
  },
  { immediate: true },
);

onMounted(() => {
  authStore.getUser();
});
</script>

<style lang="scss">
#loading-nuxt {
  position: fixed;
  z-index: 99999;
  width: 100%;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.5);
  text-align: center;
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  color: var(--active);
  justify-content: center;
  align-content: center;
  text-align: center;
  & > div {
    margin: 0 auto;
  }
}
</style>
